import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Phone, Building2, FileText, Globe, Sparkles, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const BUSINESS_TYPES = [
  { value: 'restaurant', label: { es: 'Restaurante / Bar', en: 'Restaurant / Bar', fr: 'Restaurant / Bar' } },
  { value: 'beauty', label: { es: 'Peluquería / Estética', en: 'Hair Salon / Beauty', fr: 'Salon de coiffure / Beauté' } },
  { value: 'retail', label: { es: 'Tienda / Retail', en: 'Retail Store', fr: 'Commerce de détail' } },
  { value: 'health', label: { es: 'Salud / Bienestar', en: 'Health / Wellness', fr: 'Santé / Bien-être' } },
  { value: 'services', label: { es: 'Servicios Profesionales', en: 'Professional Services', fr: 'Services professionnels' } },
  { value: 'automotive', label: { es: 'Automoción', en: 'Automotive', fr: 'Automobile' } },
  { value: 'education', label: { es: 'Educación / Formación', en: 'Education / Training', fr: 'Éducation / Formation' } },
  { value: 'other', label: { es: 'Otro', en: 'Other', fr: 'Autre' } }
];

// Textos traducidos
const TEXTS = {
  es: {
    demoActivated: '¡Demo Activada!',
    smsSent: 'Hemos enviado un SMS a tu teléfono. Responde para experimentar cómo nuestra IA reactivaría a tus clientes.',
    yourNumber: 'Tu número',
    sessionExpires: 'La sesión expira en 24 horas. Puedes tener una conversación completa con el agente.',
    businessInfo: 'Información del Negocio',
    businessName: 'Nombre del Negocio',
    businessNamePlaceholder: 'Ej: La Trattoria de María',
    businessType: 'Tipo de Negocio',
    selectOption: 'Selecciona...',
    businessDescription: 'Descripción del Negocio',
    businessDescPlaceholder: 'Describe tu negocio, productos/servicios principales, qué te hace especial...',
    websiteOptional: 'Página Web (opcional)',
    websiteHint: 'Si tienes web, la IA aprenderá sobre tu negocio automáticamente',
    reactivationOffer: 'Oferta de Reactivación',
    whatOffer: '¿Qué ofrecerás a los clientes inactivos?',
    offerPlaceholder: 'Ej: 20% de descuento en tu próxima visita',
    yourContact: 'Tu Contacto (para la Demo)',
    yourName: 'Tu Nombre',
    namePlaceholder: 'Ej: María García',
    yourPhone: 'Tu Teléfono Móvil',
    phoneHint: 'Recibirás un SMS de prueba en este número',
    yourEmail: 'Tu Email (para seguimiento)',
    emailHint: 'Te enviaremos información sobre cómo activar tu reactivación completa',
    dpaAccept: 'He leído y acepto el',
    dpaLink: 'Acuerdo de Procesamiento de Datos',
    dpaUnderstand: '. Entiendo que LokAlGrow actuará como procesador de los datos de mis clientes.',
    tryAgent: 'Probar el Agente IA Ahora',
    startingDemo: 'Iniciando Demo...',
    noRegistration: 'No se requiere registro. Recibirás un SMS en menos de 30 segundos.',
    errorDpa: 'Debes aceptar el Acuerdo de Procesamiento de Datos',
    errorPhone: 'Introduce un número de teléfono válido',
    successDemo: '¡Demo iniciada! Revisa tus SMS',
    errorSms: 'No pudimos enviar el SMS. Por ahora solo soportamos España (+34) y Francia (+33). Prueba con un número de estos países.',
    errorGeneric: 'Error al iniciar la demo'
  },
  en: {
    demoActivated: 'Demo Activated!',
    smsSent: 'We sent an SMS to your phone. Reply to experience how our AI would reactivate your customers.',
    yourNumber: 'Your number',
    sessionExpires: 'Session expires in 24 hours. You can have a complete conversation with the agent.',
    businessInfo: 'Business Information',
    businessName: 'Business Name',
    businessNamePlaceholder: 'E.g.: Maria\'s Trattoria',
    businessType: 'Business Type',
    selectOption: 'Select...',
    businessDescription: 'Business Description',
    businessDescPlaceholder: 'Describe your business, main products/services, what makes you special...',
    websiteOptional: 'Website (optional)',
    websiteHint: 'If you have a website, the AI will learn about your business automatically',
    reactivationOffer: 'Reactivation Offer',
    whatOffer: 'What will you offer inactive customers?',
    offerPlaceholder: 'E.g.: 20% off your next visit',
    yourContact: 'Your Contact (for the Demo)',
    yourName: 'Your Name',
    namePlaceholder: 'E.g.: Maria Garcia',
    yourPhone: 'Your Mobile Phone',
    phoneHint: 'You will receive a test SMS on this number',
    yourEmail: 'Your Email (for follow-up)',
    emailHint: 'We will send you information on how to activate your full reactivation',
    dpaAccept: 'I have read and accept the',
    dpaLink: 'Data Processing Agreement',
    dpaUnderstand: '. I understand that LokAlGrow will act as a processor of my customer data.',
    tryAgent: 'Try the AI Agent Now',
    startingDemo: 'Starting Demo...',
    noRegistration: 'No registration required. You will receive an SMS in less than 30 seconds.',
    errorDpa: 'You must accept the Data Processing Agreement',
    errorPhone: 'Enter a valid phone number',
    successDemo: 'Demo started! Check your SMS',
    errorSms: 'We could not send the SMS. Currently we only support Spain (+34) and France (+33). Please try with a number from these countries.',
    errorGeneric: 'Error starting demo'
  },
  fr: {
    demoActivated: 'Démo Activée !',
    smsSent: 'Nous avons envoyé un SMS à votre téléphone. Répondez pour découvrir comment notre IA réactiverait vos clients.',
    yourNumber: 'Votre numéro',
    sessionExpires: 'La session expire dans 24 heures. Vous pouvez avoir une conversation complète avec l\'agent.',
    businessInfo: 'Informations sur l\'entreprise',
    businessName: 'Nom de l\'entreprise',
    businessNamePlaceholder: 'Ex: La Trattoria de Maria',
    businessType: 'Type d\'entreprise',
    selectOption: 'Sélectionner...',
    businessDescription: 'Description de l\'entreprise',
    businessDescPlaceholder: 'Décrivez votre entreprise, vos principaux produits/services, ce qui vous rend spécial...',
    websiteOptional: 'Site Web (optionnel)',
    websiteHint: 'Si vous avez un site web, l\'IA apprendra automatiquement sur votre entreprise',
    reactivationOffer: 'Offre de Réactivation',
    whatOffer: 'Que proposerez-vous aux clients inactifs ?',
    offerPlaceholder: 'Ex: 20% de réduction sur votre prochaine visite',
    yourContact: 'Vos Coordonnées (pour la Démo)',
    yourName: 'Votre Nom',
    namePlaceholder: 'Ex: Maria Garcia',
    yourPhone: 'Votre Téléphone Mobile',
    phoneHint: 'Vous recevrez un SMS de test sur ce numéro',
    yourEmail: 'Votre Email (pour le suivi)',
    emailHint: 'Nous vous enverrons des informations sur comment activer votre réactivation complète',
    dpaAccept: 'J\'ai lu et j\'accepte l\'',
    dpaLink: 'Accord de Traitement des Données',
    dpaUnderstand: '. Je comprends que LokAlGrow agira en tant que sous-traitant des données de mes clients.',
    tryAgent: 'Essayer l\'Agent IA Maintenant',
    startingDemo: 'Démarrage de la Démo...',
    noRegistration: 'Aucune inscription requise. Vous recevrez un SMS en moins de 30 secondes.',
    errorDpa: 'Vous devez accepter l\'Accord de Traitement des Données',
    errorPhone: 'Entrez un numéro de téléphone valide',
    successDemo: 'Démo démarrée ! Vérifiez vos SMS',
    errorSms: 'Nous n\'avons pas pu envoyer le SMS. Actuellement, nous ne supportons que l\'Espagne (+34) et la France (+33). Veuillez essayer avec un numéro de ces pays.',
    errorGeneric: 'Erreur lors du démarrage de la démo'
  }
};

export const DemoAgentForm = ({ onDemoStarted }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('es') ? 'es' : i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const t = TEXTS[lang];
  
  const [formData, setFormData] = useState({
    business_name: '',
    business_type: '',
    business_description: '',
    business_website: '',
    target_offer: '',
    owner_name: '',
    owner_phone: '',
    owner_email: '',
    accepted_dpa: false
  });
  
  const [loading, setLoading] = useState(false);
  const [demoStarted, setDemoStarted] = useState(false);
  const [demoId, setDemoId] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.accepted_dpa) {
      toast.error(t.errorDpa);
      return;
    }
    
    if (!formData.owner_phone.match(/^\+?[0-9]{9,15}$/)) {
      toast.error(t.errorPhone);
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/reactivation/demo/start`, {
        ...formData,
        language: lang
      });
      
      if (response.data.success) {
        // Verificar si el SMS se envió correctamente
        if (response.data.sms_sent) {
          setDemoStarted(true);
          setDemoId(response.data.demo_id);
          toast.success(t.successDemo);
          
          if (onDemoStarted) {
            onDemoStarted(response.data);
          }
        } else {
          // SMS no enviado - probablemente país no soportado
          toast.error(t.errorSms);
        }
      }
    } catch (error) {
      console.error('Error starting demo:', error);
      toast.error(error.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  if (demoStarted) {
    return (
      <motion.div 
        className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 text-center"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          {t.demoActivated}
        </h3>
        <p className="text-gray-600 mb-6">
          {t.smsSent}
        </p>
        <div className="bg-white rounded-xl p-4 inline-block">
          <p className="text-sm text-gray-500 mb-1">{t.yourNumber}</p>
          <p className="text-lg font-mono font-bold text-gray-900">{formData.owner_phone}</p>
        </div>
        <p className="text-sm text-gray-500 mt-6">
          {t.sessionExpires}
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Business Info Section */}
      <div className="space-y-4">
        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-amber-600" />
          {t.businessInfo}
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              {t.businessName} *
            </label>
            <Input
              value={formData.business_name}
              onChange={(e) => handleChange('business_name', e.target.value)}
              placeholder={t.businessNamePlaceholder}
              required
              data-testid="demo-business-name"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              {t.businessType} *
            </label>
            <select
              value={formData.business_type}
              onChange={(e) => handleChange('business_type', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              required
              data-testid="demo-business-type"
            >
              <option value="">{t.selectOption}</option>
              {BUSINESS_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label[lang] || type.label.en}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">
            {t.businessDescription} *
          </label>
          <Textarea
            value={formData.business_description}
            onChange={(e) => handleChange('business_description', e.target.value)}
            placeholder={t.businessDescPlaceholder}
            rows={3}
            required
            data-testid="demo-business-description"
          />
        </div>
        
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">
            <Globe className="w-4 h-4 inline mr-1" />
            {t.websiteOptional}
          </label>
          <Input
            value={formData.business_website}
            onChange={(e) => handleChange('business_website', e.target.value)}
            placeholder="https://www.tunegocio.com"
            type="url"
            data-testid="demo-business-website"
          />
          <p className="text-xs text-gray-500 mt-1">
            {t.websiteHint}
          </p>
        </div>
      </div>

      {/* Offer Section */}
      <div className="space-y-4">
        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-600" />
          {t.reactivationOffer}
        </h4>
        
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">
            {t.whatOffer} *
          </label>
          <Input
            value={formData.target_offer}
            onChange={(e) => handleChange('target_offer', e.target.value)}
            placeholder={t.offerPlaceholder}
            required
            data-testid="demo-target-offer"
          />
        </div>
      </div>

      {/* Contact Section */}
      <div className="space-y-4">
        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
          <Phone className="w-5 h-5 text-amber-600" />
          {t.yourContact}
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              {t.yourName} *
            </label>
            <Input
              value={formData.owner_name}
              onChange={(e) => handleChange('owner_name', e.target.value)}
              placeholder={t.namePlaceholder}
              required
              data-testid="demo-owner-name"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              {t.yourPhone} *
            </label>
            <Input
              value={formData.owner_phone}
              onChange={(e) => handleChange('owner_phone', e.target.value)}
              placeholder="+34 612 345 678"
              required
              data-testid="demo-owner-phone"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t.phoneHint}
            </p>
          </div>
        </div>
        
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">
            {t.yourEmail}
          </label>
          <Input
            value={formData.owner_email}
            onChange={(e) => handleChange('owner_email', e.target.value)}
            placeholder="tu@email.com"
            type="email"
            data-testid="demo-owner-email"
          />
          <p className="text-xs text-gray-500 mt-1">
            {t.emailHint}
          </p>
        </div>
      </div>

      {/* DPA Acceptance */}
      <div className="bg-gray-50 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Checkbox
            id="dpa-acceptance"
            checked={formData.accepted_dpa}
            onCheckedChange={(checked) => handleChange('accepted_dpa', checked)}
            data-testid="demo-dpa-checkbox"
          />
          <label htmlFor="dpa-acceptance" className="text-sm text-gray-600 cursor-pointer">
            {t.dpaAccept}
            <a href="/legal/dpa" target="_blank" className="text-amber-600 hover:underline">
              {t.dpaLink}
            </a>
            {t.dpaUnderstand}
          </label>
        </div>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={loading || !formData.accepted_dpa}
        className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-6 text-lg font-semibold rounded-xl"
        data-testid="demo-submit-button"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            {t.startingDemo}
          </>
        ) : (
          <>
            <Sparkles className="w-5 h-5 mr-2" />
            {t.tryAgent}
          </>
        )}
      </Button>
      
      <p className="text-center text-xs text-gray-500">
        {t.noRegistration}
      </p>
    </form>
  );
};

export default DemoAgentForm;
